import { ReferralSummary } from '../../../../models/referral/referral.model';
import { SectionDescription, SectionHeading, Card, FieldRow, PrimaryActionButton, ReferralInput, SocialActions, SocialButton, StatusNotice } from '../Referral.styles';
import ReferralShareMessage from './ReferralShareMessage';
import useReferralShare from '../../../../hooks/dashboard/referrals/useReferralShare';
import { LinkedinSolid } from '../../../../components/Icons/LinkedIn';
import InstagramIcon from '../../../../components/Icons/InstagramIcon';
import { FacebookSolid } from '../../../../components/Icons/Facebook';
import { TelegramFill } from '../../../../components/Icons/TelegramFill';
import { WhatsappFill } from '../../../../components/Icons/WhatsappFill';

interface ReferralLinkCardProps {
  summary: ReferralSummary | null;
  loading?: boolean;
}

const ReferralLinkCard: React.FC<ReferralLinkCardProps> = ({
  summary,
  loading,
}) => {
  const referralLink = summary?.referralLink ?? '';
  const isEligible = summary?.isEligible ?? false;
  const isShareDisabled = loading || !referralLink;
  const buttonLabel = loading ? 'Loading...' : 'Invite & Earn Now';
  const {
    message,
    setMessage,
    copyLink,
    copyMessage,
    whatsapp,
    telegram,
    linkedin,
    facebook,
    instagram,
  } = useReferralShare(referralLink);

  return (
    <Card>
      <SectionHeading>Earn Free Rewards when you refer a Friend</SectionHeading>
      <SectionDescription>
        Share your referral link with healthcare professionals and recruiters,
        and earn rewards when they complete verification.
      </SectionDescription>

      {!isEligible && (
        <StatusNotice>
          You can still share your link, but rewards are only counted once
          your account is fully verified and reaches Tier 3. Locum accounts
          must also be active.
        </StatusNotice>
      )}

      <SectionHeading as="h3">Get Your Referral Link</SectionHeading>
      <SectionDescription>
        Share this link with your friends. Rewards count when referred accounts
        complete verification and reach Tier 3.
      </SectionDescription>

      <FieldRow>
        <ReferralInput
          readOnly
          value={referralLink || 'Your referral link will appear here once available.'}
        />
        <PrimaryActionButton
          type="button"
          disabled={isShareDisabled}
          onClick={copyLink}
        >
          {buttonLabel}
        </PrimaryActionButton>
      </FieldRow>

      <SocialActions>
        <SocialButton
          type="button"
          onClick={linkedin}
          aria-label="Share on LinkedIn"
          disabled={isShareDisabled}
        >
          <LinkedinSolid />
        </SocialButton>
        <SocialButton
          type="button"
          onClick={instagram}
          aria-label="Share on Instagram"
          disabled={isShareDisabled}
        >
          <InstagramIcon />
        </SocialButton>
        <SocialButton
          type="button"
          onClick={facebook}
          aria-label="Share on Facebook"
          disabled={isShareDisabled}
        >
          <FacebookSolid width="24px" height="24px" color="#1877F2" />
        </SocialButton>
        <SocialButton
          type="button"
          onClick={telegram}
          aria-label="Share on Telegram"
          disabled={isShareDisabled}
        >
          <TelegramFill />
        </SocialButton>
        <SocialButton
          type="button"
          onClick={whatsapp}
          aria-label="Share on WhatsApp"
          disabled={isShareDisabled}
        >
          <WhatsappFill />
        </SocialButton>
      </SocialActions>

      <ReferralShareMessage
        message={message}
        onMessageChange={setMessage}
        onCopyMessage={copyMessage}
        disabled={isShareDisabled}
      />
    </Card>
  );
};

export default ReferralLinkCard;
